
import { useState, useEffect, useCallback, useRef } from 'react';
import { Modal } from '../../../../components/base/Modal';
import { supabase } from '../../../../lib/supabase';
import { useAuth } from '../../../../hooks/useAuth';

interface UserResult {
  id: string;
  username: string;
  avatar_url: string;
  bio: string | null;
  is_verified_creator: boolean;
  reputation_points: number;
  isAlreadyMember: boolean;
  isInvited: boolean;
}

interface InviteMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
  groupName: string;
  groupUrl: string;
  groupDescription?: string;
  existingMemberIds: string[];
  onMemberInvited?: () => void;
}

const InviteMembersModal = ({
  isOpen,
  onClose,
  groupId,
  groupName,
  groupUrl,
  groupDescription,
  existingMemberIds,
  onMemberInvited,
}: InviteMembersModalProps) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'search' | 'link' | 'email'>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [invitedIds, setInvitedIds] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState(false);
  const [emails, setEmails] = useState('');
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailsSent, setEmailsSent] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const inviteMessage = `Join me in "${groupName}" - a community for drama fans! ${groupUrl}`;

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('id, username, avatar_url, bio, is_verified_creator, reputation_points')
          .ilike('username', `%${searchQuery.trim()}%`)
          .neq('id', user?.id || '')
          .eq('is_banned', false)
          .limit(20);

        if (error) {
          console.error('Search error:', error);
          setSearchResults([]);
        } else {
          const results: UserResult[] = (data || []).map((u) => ({
            ...u,
            bio: u.bio || null,
            is_verified_creator: u.is_verified_creator || false,
            reputation_points: u.reputation_points || 0,
            isAlreadyMember: existingMemberIds.includes(u.id),
            isInvited: invitedIds.has(u.id),
          }));
          setSearchResults(results);
        }
      } catch (err) {
        console.error('Search failed:', err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, user?.id, existingMemberIds, invitedIds]);

  const handleInviteUser = useCallback(async (targetUser: UserResult) => {
    if (!user || targetUser.isAlreadyMember) return;

    try {
      // Create a notification for the invited user
      await supabase.from('notifications').insert({
        user_id: targetUser.id,
        type: 'group_invite',
        title: 'Group Invitation',
        message: `${user.user_metadata?.username || 'Someone'} invited you to join "${groupName}"`,
        link: groupUrl,
        is_read: false,
      });

      setInvitedIds((prev) => new Set([...prev, targetUser.id]));
      
      // Update search results to reflect invited status
      setSearchResults((prev) =>
        prev.map((r) => (r.id === targetUser.id ? { ...r, isInvited: true } : r))
      );

      showNotification(`Invitation sent to ${targetUser.username}`, 'success');
      onMemberInvited?.();
    } catch (err) {
      console.error('Failed to invite user:', err);
      showNotification('Failed to send invitation', 'error');
    }
  }, [user, groupName, groupUrl, onMemberInvited]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(groupUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(inviteMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSendEmails = async () => {
    if (!emails.trim()) return;
    setSendingEmails(true);
    await new Promise((resolve) => setTimeout(resolve, 1200));
    setSendingEmails(false);
    setEmailsSent(true);
    setEmails('');
    setTimeout(() => setEmailsSent(false), 3000);
  };

  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const socialShareOptions = [
    {
      name: 'WhatsApp',
      icon: 'ri-whatsapp-line',
      color: 'bg-green-500 hover:bg-green-600',
      action: () => window.open(`https://wa.me/?text=${encodeURIComponent(inviteMessage)}`, '_blank'),
    },
    {
      name: 'Telegram',
      icon: 'ri-telegram-line',
      color: 'bg-sky-500 hover:bg-sky-600',
      action: () => window.open(`https://t.me/share/url?url=${encodeURIComponent(groupUrl)}&text=${encodeURIComponent(`Join "${groupName}"!`)}`, '_blank'),
    },
    {
      name: 'Twitter',
      icon: 'ri-twitter-x-line',
      color: 'bg-gray-900 hover:bg-black',
      action: () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Join "${groupName}" - a community for drama fans!`)}&url=${encodeURIComponent(groupUrl)}`, '_blank'),
    },
    {
      name: 'Facebook',
      icon: 'ri-facebook-line',
      color: 'bg-blue-600 hover:bg-blue-700',
      action: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(groupUrl)}`, '_blank'),
    },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Invite Members" size="lg">
      {/* Notification Toast */}
      {notification && (
        <div className={`mb-4 p-3 rounded-lg text-sm flex items-center gap-2 ${
          notification.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          <i className={notification.type === 'success' ? 'ri-check-circle-line' : 'ri-error-warning-line'}></i>
          {notification.message}
        </div>
      )}

      {/* Group Preview */}
      <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-4 border border-teal-100 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-teal-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="ri-group-line text-white text-xl"></i>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">{groupName}</h3>
            <p className="text-sm text-gray-500">Invite friends and community members</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-5">
        <button
          onClick={() => setActiveTab('search')}
          className={`px-4 py-2.5 font-medium text-sm transition-colors cursor-pointer whitespace-nowrap ${
            activeTab === 'search'
              ? 'text-teal-600 border-b-2 border-teal-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <i className="ri-search-line mr-1.5"></i>
          Find Users
        </button>
        <button
          onClick={() => setActiveTab('link')}
          className={`px-4 py-2.5 font-medium text-sm transition-colors cursor-pointer whitespace-nowrap ${
            activeTab === 'link'
              ? 'text-teal-600 border-b-2 border-teal-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <i className="ri-link mr-1.5"></i>
          Share Link
        </button>
        <button
          onClick={() => setActiveTab('email')}
          className={`px-4 py-2.5 font-medium text-sm transition-colors cursor-pointer whitespace-nowrap ${
            activeTab === 'email'
              ? 'text-teal-600 border-b-2 border-teal-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <i className="ri-mail-line mr-1.5"></i>
          Email Invite
        </button>
      </div>

      <div className="max-h-[55vh] overflow-y-auto pr-1">
        {/* Search Users Tab */}
        {activeTab === 'search' && (
          <div>
            <div className="relative mb-4">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by username..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                autoFocus
              />
              {searching && (
                <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
                  <i className="ri-loader-4-line animate-spin text-gray-400"></i>
                </div>
              )}
            </div>

            {searchQuery.trim().length < 2 && searchResults.length === 0 && (
              <div className="text-center py-10">
                <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-user-search-line text-2xl text-gray-400"></i>
                </div>
                <p className="text-sm text-gray-500 mb-1">Search for users to invite</p>
                <p className="text-xs text-gray-400">Type at least 2 characters to search</p>
              </div>
            )}

            {searchQuery.trim().length >= 2 && !searching && searchResults.length === 0 && (
              <div className="text-center py-10">
                <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-user-unfollow-line text-2xl text-gray-400"></i>
                </div>
                <p className="text-sm text-gray-500">No users found for &quot;{searchQuery}&quot;</p>
              </div>
            )}

            {searchResults.length > 0 && (
              <div className="space-y-1">
                {searchResults.map((result) => (
                  <div
                    key={result.id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <img
                      src={result.avatar_url || `https://ui-avatars.com/api/?name=${result.username}&background=14b8a6&color=fff`}
                      alt={result.username}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-sm text-gray-900 truncate">{result.username}</span>
                        {result.is_verified_creator && (
                          <i className="ri-verified-badge-fill text-teal-500 text-sm"></i>
                        )}
                      </div>
                      {result.bio && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">{result.bio}</p>
                      )}
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-gray-400">
                          <i className="ri-star-line mr-0.5"></i>
                          {result.reputation_points} rep
                        </span>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {result.isAlreadyMember ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-500 rounded-lg text-xs font-medium whitespace-nowrap">
                          <i className="ri-check-line"></i>
                          Member
                        </span>
                      ) : result.isInvited || invitedIds.has(result.id) ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-lg text-xs font-medium whitespace-nowrap">
                          <i className="ri-check-line"></i>
                          Invited
                        </span>
                      ) : (
                        <button
                          onClick={() => handleInviteUser(result)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-500 hover:bg-teal-600 text-white rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-user-add-line"></i>
                          Invite
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {invitedIds.size > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <i className="ri-check-double-line text-emerald-500"></i>
                  {invitedIds.size} invitation{invitedIds.size > 1 ? 's' : ''} sent this session
                </p>
              </div>
            )}
          </div>
        )}

        {/* Share Link Tab */}
        {activeTab === 'link' && (
          <div className="space-y-5">
            {/* Copy Link */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2.5">Invite link</h4>
              <div className="flex items-center gap-2">
                <div className="flex-1 flex items-center gap-2 px-3.5 py-2.5 bg-gray-50 rounded-lg border border-gray-200">
                  <i className="ri-link text-gray-400"></i>
                  <input
                    type="text"
                    value={groupUrl}
                    readOnly
                    className="flex-1 bg-transparent text-sm text-gray-600 outline-none min-w-0"
                  />
                </div>
                <button
                  onClick={handleCopyLink}
                  className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap ${
                    copied ? 'bg-emerald-500 text-white' : 'bg-teal-500 hover:bg-teal-600 text-white'
                  }`}
                >
                  {copied ? (
                    <span className="flex items-center gap-1">
                      <i className="ri-check-line"></i> Copied!
                    </span>
                  ) : (
                    'Copy'
                  )}
                </button>
              </div>
            </div>

            {/* Copy Message */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2.5">Invite message</h4>
              <div className="relative">
                <div className="p-3.5 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-600 pr-20">
                  {inviteMessage}
                </div>
                <button
                  onClick={handleCopyMessage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 text-teal-600 hover:bg-teal-50 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Social Share */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2.5">Share via</h4>
              <div className="flex flex-wrap gap-2">
                {socialShareOptions.map((option) => (
                  <button
                    key={option.name}
                    onClick={option.action}
                    className={`flex items-center gap-2 px-4 py-2.5 ${option.color} text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap`}
                  >
                    <i className={`${option.icon} text-lg`}></i>
                    <span className="text-sm font-medium">{option.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Email Invite Tab */}
        {activeTab === 'email' && (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2.5">Send email invitations</h4>
              <textarea
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                placeholder="Enter email addresses separated by commas...&#10;e.g. friend1@email.com, friend2@email.com"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                rows={4}
                maxLength={500}
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-gray-500">
                  <i className="ri-information-line mr-1"></i>
                  Separate multiple emails with commas
                </p>
                <button
                  onClick={handleSendEmails}
                  disabled={!emails.trim() || sendingEmails}
                  className={`px-5 py-2 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap ${
                    emailsSent
                      ? 'bg-emerald-500 text-white'
                      : emails.trim()
                        ? 'bg-teal-500 hover:bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {sendingEmails ? (
                    <span className="flex items-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending...
                    </span>
                  ) : emailsSent ? (
                    <span className="flex items-center gap-1">
                      <i className="ri-check-line"></i>
                      Sent!
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <i className="ri-send-plane-line"></i>
                      Send Invites
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Email Preview */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
              <p className="text-xs text-gray-500 mb-2 font-medium">Email preview</p>
              <div className="bg-white rounded-lg p-4 border border-gray-100">
                <p className="text-sm font-semibold text-gray-900 mb-1">You&apos;re invited to join {groupName}!</p>
                <p className="text-xs text-gray-600 leading-relaxed">
                  Hey! I wanted to invite you to join &quot;{groupName}&quot; - a community for drama fans.
                  {groupDescription && ` ${groupDescription.substring(0, 100)}...`}
                </p>
                <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-500 text-white rounded-md text-xs font-medium">
                  <i className="ri-group-line"></i>
                  Join Group
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default InviteMembersModal;
